import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { meetingsKeys } from "@/application/query-keys/meetings.keys";
import {
  meetingsRepository,
  type MeetingDetail,
  type MeetingFilters,
} from "@/infrastructure/repositories/meetings.supabase.repository";
import type { EntityType, MeetingRow } from "@/infrastructure/supabase/database.types";

export function useMeetingsList(filters: MeetingFilters = {}) {
  return useQuery({
    queryKey: meetingsKeys.list(filters),
    queryFn: () => meetingsRepository.list(filters),
  });
}

export function useMeetingDetail(id: string | undefined) {
  return useQuery({
    queryKey: meetingsKeys.detail(id ?? ""),
    queryFn: () => meetingsRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useMeetingBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: meetingsKeys.bySlug(slug ?? ""),
    queryFn: () => meetingsRepository.bySlug(slug as string),
    enabled: !!slug,
  });
}

export function useMeetingsForEntity(entityType: EntityType, entityId: string | undefined) {
  return useQuery({
    queryKey: meetingsKeys.forEntity(entityType, entityId ?? ""),
    queryFn: () => meetingsRepository.meetingsForEntity(entityType, entityId as string),
    enabled: !!entityId,
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<MeetingRow, "id" | "created_at" | "updated_at">) =>
      meetingsRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.all });
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<MeetingRow> }) =>
      meetingsRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.all });
      queryClient.setQueryData<MeetingDetail | undefined>(meetingsKeys.detail(data.id), (prev) =>
        prev ? { ...prev, ...data } : undefined
      );
    },
  });
}

export function useDeleteMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.all });
    },
  });
}

export function useAddMeetingLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      entityType,
      entityId,
    }: {
      meetingId: string;
      entityType: EntityType;
      entityId: string;
    }) => meetingsRepository.addLink(meetingId, entityType, entityId),
    onSuccess: (_, { meetingId, entityType, entityId }) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(meetingId) });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.forEntity(entityType, entityId) });
    },
  });
}

export function useRemoveMeetingLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId }: { linkId: string; meetingId: string }) =>
      meetingsRepository.removeLink(linkId),
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(meetingId) });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.all });
    },
  });
}

export function useAddMeetingAttendee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, stakeholderId }: { meetingId: string; stakeholderId: string }) =>
      meetingsRepository.addAttendee(meetingId, stakeholderId),
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(meetingId) });
    },
  });
}
